import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck, KeyRound, RefreshCw, RotateCcw, LogIn, ShieldAlert, Download, KeyRound as KeyRoundIcon, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import PurgeAuditHistoryDialog from "../components/PurgeAuditHistoryDialog";
import { useAuth } from "../contexts/AuthContext";
import StickyPageHeader from "../components/StickyPageHeader";

const EVENT_LABELS = {
  password_reset: { label: "Réinitialisation MDP", icon: RotateCcw, color: "text-amber-700 bg-amber-50 border-amber-200" },
  password_changed: { label: "Changement MDP", icon: KeyRound, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  password_reset_requested: { label: "Demande de réinitialisation MDP", icon: KeyRoundIcon, color: "text-purple-700 bg-purple-50 border-purple-200" },
  login_success: { label: "Connexion réussie", icon: LogIn, color: "text-blue-700 bg-blue-50 border-blue-200" },
  login_failed: { label: "Échec de connexion", icon: ShieldAlert, color: "text-red-700 bg-red-50 border-red-200" },
  access_denied: { label: "Accès refusé (RBAC)", icon: ShieldAlert, color: "text-red-700 bg-red-50 border-red-200" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AuditLogs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [logs, setLogs] = useState([]);
  const [eventFilter, setEventFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [textQ, setTextQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const buildParams = () => {
    const params = {};
    if (eventFilter && eventFilter !== "all") params.event = eventFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", { params: buildParams() });
      setLogs(data);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventFilter, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (eventFilter && eventFilter !== "all") params.set("event", eventFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await api.get(`/audit-logs/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = [dateFrom, dateTo].filter(Boolean).join("_") || new Date().toISOString().slice(0, 10);
      a.download = `audit_logs_${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'export");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setEventFilter("all");
    setDateFrom("");
    setDateTo("");
    setTextQ("");
  };

  const filteredLogs = useMemo(() => {
    const q = textQ.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      `${l.actor_name || ""} ${l.actor_email || ""} ${l.target_name || ""} ${l.target_email || ""} ${l.details || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [logs, textQ]);

  const stats = useMemo(() => {
    return {
      total: logs.length,
      logins_ok: logs.filter((l) => l.event === "login_success").length,
      logins_failed: logs.filter((l) => l.event === "login_failed").length,
      resets: logs.filter((l) => l.event === "password_reset").length,
      changes: logs.filter((l) => l.event === "password_changed").length,
    };
  }, [logs]);

  return (
    <div className="space-y-8" data-testid="audit-page">
      <StickyPageHeader testid="audit-sticky-header">
      <div>
        <p className="label-uppercase mb-2">Sécurité & Conformité</p>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <ShieldCheck size={28} className="text-emerald-600" />
          Historique de sécurité
        </h1>
        <p className="text-sm text-zinc-600 mt-2">
          Journal d&apos;audit des événements liés aux mots de passe (réinitialisations administrateur, changements utilisateur).
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="border border-zinc-200 bg-white p-5" data-testid="audit-kpi-total">
          <p className="label-uppercase">Total événements</p>
          <p className="text-3xl font-display font-bold mt-1">{stats.total}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="audit-kpi-logins-ok">
          <p className="label-uppercase">Connexions réussies</p>
          <p className="text-3xl font-display font-bold mt-1 text-blue-700">{stats.logins_ok}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="audit-kpi-logins-failed">
          <p className="label-uppercase">Échecs de connexion</p>
          <p className="text-3xl font-display font-bold mt-1 text-red-700">{stats.logins_failed}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="audit-kpi-resets">
          <p className="label-uppercase">Réinitialisations admin</p>
          <p className="text-3xl font-display font-bold mt-1 text-amber-700">{stats.resets}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="audit-kpi-changes">
          <p className="label-uppercase">Changements utilisateurs</p>
          <p className="text-3xl font-display font-bold mt-1 text-emerald-700">{stats.changes}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <p className="label-uppercase mb-1">Recherche</p>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              type="text"
              value={textQ}
              onChange={(e) => setTextQ(e.target.value)}
              placeholder="Rechercher par auteur, cible, détails…"
              className="pl-8 h-10"
              data-testid="audit-text-search"
            />
          </div>
        </div>
        <div className="min-w-[220px]">
          <p className="label-uppercase mb-1">Type d&apos;événement</p>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger data-testid="audit-event-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les événements</SelectItem>
              <SelectItem value="login_success">Connexion réussie</SelectItem>
              <SelectItem value="login_failed">Échec de connexion</SelectItem>
              <SelectItem value="password_reset">Réinitialisation MDP</SelectItem>
              <SelectItem value="password_reset_requested">Demande de réinitialisation MDP</SelectItem>
              <SelectItem value="password_changed">Changement MDP</SelectItem>
              <SelectItem value="access_denied">Accès refusé (RBAC)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="label-uppercase mb-1">Du</p>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-10"
            data-testid="audit-date-from"
          />
        </div>
        <div>
          <p className="label-uppercase mb-1">Au</p>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-10"
            data-testid="audit-date-to"
          />
        </div>
        {(eventFilter !== "all" || dateFrom || dateTo || textQ) && (
          <Button
            variant="ghost"
            onClick={resetFilters}
            className="text-zinc-600 hover:text-zinc-900"
            data-testid="audit-reset-filters"
          >
            Réinitialiser
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
          <Button
            onClick={exportCsv}
            disabled={exporting}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
            data-testid="audit-export-csv"
          >
            <Download size={14} className="mr-1" />
            {exporting ? "Export…" : "Exporter CSV"}
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setPurgeOpen(true)}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="audit-purge-btn"
              title="Purger l'historique de sécurité"
            >
              <Trash2 size={14} className="mr-1" />
              Purger l&apos;historique
            </Button>
          )}
        </div>
      </div>
      </StickyPageHeader>

      {/* Table */}
      <HorizontalScrollContainer testId="audit-table-scroll" className="border border-zinc-200 bg-white">
        <table className="w-full text-sm min-w-[960px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left p-3 label-uppercase whitespace-nowrap">Date</th>
              <th className="text-left p-3 label-uppercase whitespace-nowrap">Événement</th>
              <th className="text-left p-3 label-uppercase whitespace-nowrap">Auteur</th>
              <th className="text-left p-3 label-uppercase whitespace-nowrap">Cible</th>
              <th className="text-left p-3 label-uppercase whitespace-nowrap">Détails</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => {
              const meta = EVENT_LABELS[log.event] || { label: log.event, icon: ShieldCheck, color: "text-zinc-700 bg-zinc-50 border-zinc-200" };
              const Icon = meta.icon;
              return (
                <tr key={log.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`audit-row-${log.id}`}>
                  <td className="p-3 font-mono text-xs text-zinc-600 whitespace-nowrap">{formatDate(log.created_at)}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-xs font-medium ${meta.color}`}>
                      <Icon size={12} /> {meta.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <p className="font-medium text-zinc-900">{log.actor_name || "—"}</p>
                    <p className="font-mono text-xs text-zinc-500">{log.actor_email || ""}</p>
                  </td>
                  <td className="p-3">
                    {log.target_id && log.target_id !== log.actor_id ? (
                      <>
                        <p className="font-medium text-zinc-900">{log.target_name || "—"}</p>
                        <p className="font-mono text-xs text-zinc-500">{log.target_email || ""}</p>
                      </>
                    ) : (
                      <span className="text-xs text-zinc-400 font-mono">— (lui-même)</span>
                    )}
                  </td>
                  <td className="p-3 text-zinc-600">{log.details || "—"}</td>
                </tr>
              );
            })}
            {filteredLogs.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center p-8 text-sm text-zinc-500 font-mono">
                  {logs.length === 0 ? "Aucun événement enregistré" : "Aucun événement ne correspond à votre recherche"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      {isAdmin && (
        <PurgeAuditHistoryDialog
          open={purgeOpen}
          onOpenChange={setPurgeOpen}
          onDone={() => load()}
        />
      )}
    </div>
  );
}
